import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CanAccess,
  useCanAccess,
  useDataProvider,
  useNotify,
  useTranslate,
} from "ra-core";
import { Navigate, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Project, ProfileOption, SalesTool } from "../types";
import { SalesToolDetail } from "./SalesToolDetail";
import { SalesToolForm } from "./SalesToolForm";
import {
  EMPTY_TOOL_FILTERS,
  SALES_TOOL_KINDS,
  matchesToolFilters,
  type SalesToolFilters,
} from "./salesToolRelevance";
import { SALES_TOOLS_QUERY_KEY, useSalesTools } from "./useSalesTools";

const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-sm sm:h-9";

/**
 * Almikia Epic EH (TASK-013): "Biblioteca de herramientas". Read-only browse of the sales tools
 * the role may see, with filters by kind, stage, project and client type plus text search. The
 * list is one bounded query filtered client-side; ordering is alphabetical, never by usage or
 * any per-advisor data. The lead screen links here with `?stage=&project=` prefilled.
 */
export const SalesToolLibrary = () => (
  <CanAccess
    resource="sales_tools"
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <SalesToolLibraryContent />
  </CanAccess>
);

SalesToolLibrary.path = "/sales_tools";

const SalesToolLibraryContent = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider();
  const { dealStages } = useConfigurationContext();
  const [searchParams] = useSearchParams();
  const { canAccess: canSeeArchived } = useCanAccess({
    resource: "sales_tools",
    action: "create",
  });
  const [filters, setFilters] = useState<SalesToolFilters>(() => ({
    ...EMPTY_TOOL_FILTERS,
    stage: searchParams.get("stage") ?? "",
    projectId: searchParams.get("project") ?? "",
  }));
  const [selected, setSelected] = useState<SalesTool | null>(null);
  // undefined = form closed, null = creating, a tool = editing it.
  const [formTool, setFormTool] = useState<SalesTool | null | undefined>(
    undefined,
  );
  const canManage = canSeeArchived === true;

  /** Archive / reactivate only flips `archived_at`; a tool is never deleted. */
  const toggleArchived = async (tool: SalesTool) => {
    try {
      await dataProvider.update<SalesTool>("sales_tools", {
        id: tool.id,
        data: {
          archived_at: tool.archived_at ? null : new Date().toISOString(),
        },
        previousData: tool,
      });
      await queryClient.invalidateQueries({
        queryKey: [SALES_TOOLS_QUERY_KEY],
      });
    } catch {
      notify("crm.salesTools.form.save_error", { type: "error" });
    }
  };

  const tools = useSalesTools();
  const projects = useQuery({
    queryKey: ["sales_tools_library", "projects"],
    queryFn: async () =>
      (
        await dataProvider.getList<Project>("projects", {
          pagination: { page: 1, perPage: 200 },
          sort: { field: "name", order: "ASC" },
          filter: {},
        })
      ).data,
  });
  const buyerTypes = useQuery({
    queryKey: ["sales_tools_library", "buyer_types"],
    queryFn: async () =>
      (
        await dataProvider.getList<ProfileOption>("profile_buyer_types", {
          pagination: { page: 1, perPage: 100 },
          sort: { field: "position", order: "ASC" },
          filter: {},
        })
      ).data,
  });

  const setFilter = (patch: Partial<SalesToolFilters>) =>
    setFilters((current) => ({ ...current, ...patch }));

  const effectiveFilters = {
    ...filters,
    showArchived: filters.showArchived && canSeeArchived === true,
  };
  const visible = (tools.data ?? [])
    .filter((tool) => matchesToolFilters(tool, effectiveFilters))
    .sort(
      (a, b) =>
        SALES_TOOL_KINDS.indexOf(a.kind) - SALES_TOOL_KINDS.indexOf(b.kind) ||
        a.title.localeCompare(b.title, "es"),
    );

  return (
    <div className="mt-2 max-w-3xl mx-auto space-y-4 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <h1 className="text-xl font-semibold">
          {translate("crm.salesTools.title")}
        </h1>
        {canManage ? (
          <Button
            type="button"
            className="h-11 sm:h-9"
            onClick={() => setFormTool(null)}
          >
            {translate("crm.salesTools.form.new")}
          </Button>
        ) : null}
      </div>
      <p className="px-1 text-sm text-muted-foreground">
        {translate("crm.salesTools.intro")}
      </p>

      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="tool-search">
              {translate("crm.salesTools.filter_search")}
            </Label>
            <Input
              id="tool-search"
              type="search"
              className="h-11 sm:h-9"
              value={filters.search}
              onChange={(e) => setFilter({ search: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tool-kind">
              {translate("crm.salesTools.filter_kind")}
            </Label>
            <select
              id="tool-kind"
              className={SELECT_CLASS}
              value={filters.kind}
              onChange={(e) => setFilter({ kind: e.target.value })}
            >
              <option value="">{translate("crm.salesTools.all")}</option>
              {SALES_TOOL_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {translate(`crm.salesTools.kinds.${kind}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tool-stage">
              {translate("crm.salesTools.filter_stage")}
            </Label>
            <select
              id="tool-stage"
              className={SELECT_CLASS}
              value={filters.stage}
              onChange={(e) => setFilter({ stage: e.target.value })}
            >
              <option value="">{translate("crm.salesTools.all")}</option>
              {dealStages.map((stage) => (
                <option key={stage.value} value={stage.value}>
                  {stage.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tool-project">
              {translate("crm.salesTools.filter_project")}
            </Label>
            <select
              id="tool-project"
              className={SELECT_CLASS}
              value={filters.projectId}
              onChange={(e) => setFilter({ projectId: e.target.value })}
            >
              <option value="">{translate("crm.salesTools.all")}</option>
              {(projects.data ?? []).map((project) => (
                <option key={project.id} value={String(project.id)}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tool-buyer-type">
              {translate("crm.salesTools.filter_buyer_type")}
            </Label>
            <select
              id="tool-buyer-type"
              className={SELECT_CLASS}
              value={filters.buyerTypeId}
              onChange={(e) => setFilter({ buyerTypeId: e.target.value })}
            >
              <option value="">{translate("crm.salesTools.all")}</option>
              {(buyerTypes.data ?? []).map((option) => (
                <option key={option.id} value={String(option.id)}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {canSeeArchived ? (
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={filters.showArchived}
                onChange={(e) => setFilter({ showArchived: e.target.checked })}
              />
              {translate("crm.salesTools.show_archived")}
            </label>
          ) : null}
        </CardContent>
      </Card>

      {tools.error ? (
        <ListErrorState
          message={translate("crm.salesTools.error")}
          onRetry={() => tools.refetch()}
        />
      ) : !tools.data ? (
        <ListCardsSkeleton rows={3} />
      ) : visible.length === 0 ? (
        (tools.data ?? []).length > 0 ? (
          <ListEmptyState
            title={translate("crm.salesTools.empty")}
            action={
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-11"
                onClick={() => setFilters(EMPTY_TOOL_FILTERS)}
              >
                {translate("crm.common.clear_filters")}
              </Button>
            }
          />
        ) : (
          <ListEmptyState
            title={translate("crm.salesTools.empty_title")}
            description={translate("crm.salesTools.empty_description")}
            action={
              canManage ? (
                <Button
                  type="button"
                  className="min-h-11"
                  onClick={() => setFormTool(null)}
                >
                  {translate("crm.salesTools.form.new")}
                </Button>
              ) : undefined
            }
          />
        )
      ) : (
        <ul className="space-y-2" data-testid="sales-tool-list">
          {visible.map((tool) => (
            <li key={tool.id} className="space-y-1">
              <button
                type="button"
                className="w-full text-left"
                onClick={() => setSelected(tool)}
              >
                <Card>
                  <CardContent className="space-y-1 p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-medium">{tool.title}</span>
                      <span className="text-sm text-muted-foreground">
                        {translate(`crm.salesTools.kinds.${tool.kind}`)}
                        {tool.archived_at
                          ? ` · ${translate("crm.salesTools.archived")}`
                          : ""}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </button>
              {canManage ? (
                <div className="flex gap-2 px-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-11 sm:h-8"
                    onClick={() => setFormTool(tool)}
                  >
                    {translate("crm.salesTools.form.edit")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-11 sm:h-8"
                    onClick={() => toggleArchived(tool)}
                  >
                    {translate(
                      tool.archived_at
                        ? "crm.salesTools.form.reactivate"
                        : "crm.salesTools.form.archive",
                    )}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <SalesToolDetail tool={selected} onClose={() => setSelected(null)} />
      {formTool !== undefined ? (
        <SalesToolForm
          tool={formTool}
          projects={projects.data ?? []}
          buyerTypes={buyerTypes.data ?? []}
          onClose={() => setFormTool(undefined)}
        />
      ) : null}
    </div>
  );
};
